/**
 * Tronic Track Integration Module
 * This module initializes the Tronic analytics tracking system (receiver)
 * as part of the SDK initialization process.
 */

export interface ReceiverConfig {
  apiKey: string;
  apiHost: string;
  debug?: boolean;
  initialPageview?: boolean;
  trackClicks?: boolean | string; // true for all, or CSS selector
  trackForms?: boolean | string; // true for all, or CSS selector
  trackOutboundLinks?: boolean;
  trackUtmParams?: boolean;
  autoTrackWidget?: boolean; // Auto-track widget interactions
}

export class ReceiverLoader {
  private static initialized = false;
  private static SNIPPET_VERSION = '0.0.1';

  /**
   * Initialize the receiver tracking system
   * This replicates the functionality of the tronic-track snippet
   */
  static initialize(config: ReceiverConfig): void {
    // Prevent double initialization
    if (this.initialized) {
      if (window.console && console.warn) {
        console.warn('Tronic receiver already initialized');
      }
      return;
    }

    // Create or get existing receiver array
    const receiver = ((window as any).receiver = (window as any).receiver || []);

    // Check if receiver was already initialized by external script
    if (receiver.initialize) {
      if (receiver.invoked) {
        window.console && console.error && console.error('Tronic snippet included twice.');
        return;
      }
    }

    // Mark as invoked to prevent double initialization
    receiver.invoked = true;

    // Define all receiver methods
    receiver.methods = [
      'screen',
      'register',
      'deregister',
      'trackSubmit',
      'trackClick',
      'trackLink',
      'trackForm',
      'identify',
      'reset',
      'track',
      'ready',
      'debug',
      'page',
      'once',
      'off',
      'on',
      'addSourceMiddleware',
      'addIntegrationMiddleware',
      'setAnonymousId',
      'addDestinationMiddleware',
      'group',
      'alias',
    ];

    // Create factory function to buffer method calls
    receiver.factory = function (methodName: string) {
      return function (...args: any[]) {
        args.unshift(methodName);
        receiver.push(args);
        return receiver;
      };
    };

    // Create buffer methods on receiver object
    for (let i = 0; i < receiver.methods.length; i++) {
      const key = receiver.methods[i];
      receiver[key] = receiver.factory(key);
    }

    // Define load function to dynamically load the full library
    receiver.load = function (_key: string, options: any) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://cdn.jsdelivr.net/gh/tronicapp/tronic-track@latest/packages/browser/dist/umd/standalone.js';

      // Add error handling
      script.onerror = function () {
        console.error('Failed to load Tronic tracking library');
      };

      script.onload = function () {
        if (config.debug) {
          console.log('Tronic tracking library loaded successfully');
        }
      };

      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        // Fallback: append to document head or body
        (document.head || document.body).appendChild(script);
      }

      receiver._loadOptions = options;
    };

    // Set version and writeKey
    receiver.SNIPPET_VERSION = this.SNIPPET_VERSION;
    receiver._writeKey = config.apiKey;

    // Extract host from apiHost URL if needed
    let apiHostClean = config.apiHost;

    // If apiHost is a full URL, extract just the host (no protocol)
    // The external tracking library will add https:// automatically
    try {
      const url = new URL(apiHostClean);
      apiHostClean = url.host; // Only the host part (e.g., "stage-api.tronic.app")
    } catch (e) {
      // If not a valid URL, use as-is but remove protocol and trailing slash
      apiHostClean = apiHostClean.replace(/^https?:\/\//, '');
      if (apiHostClean.endsWith('/')) {
        apiHostClean = apiHostClean.slice(0, -1);
      }
    }

    // Load the full library with configuration
    receiver.load('', {
      pluginSettings: {
        Tronic: {
          apiKey: config.apiKey,
          apiHost: apiHostClean,
        },
      },
      initialPageview: config.initialPageview !== false, // Default true
    });

    // Enable debug mode if requested
    if (config.debug) {
      receiver.debug();
    }

    // Send initial page view automatically
    this.sendInitialPageView(receiver, config);

    // Set up auto-tracking if configured
    this.setupAutoTracking(receiver, config);

    this.initialized = true;
  }

  /**
   * Set up automatic tracking based on configuration
   */
  private static setupAutoTracking(receiver: any, config: ReceiverConfig): void {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAutoTracking(receiver, config));
      return;
    }

    // Auto-track clicks
    if (config.trackClicks) {
      const selector = typeof config.trackClicks === 'string' ? config.trackClicks : 'a, button, [data-track-click]';
      setTimeout(() => {
        const elements = document.querySelectorAll(selector);
        if (elements.length > 0) {
          receiver.trackClick(elements, 'Element Clicked', {
            auto_tracked: true,
          });
          if (config.debug) {
            console.log(`[ReceiverLoader] Auto-tracking clicks on ${elements.length} elements`);
          }
        }
      }, 500);
    }

    // Auto-track forms
    if (config.trackForms) {
      const selector = typeof config.trackForms === 'string' ? config.trackForms : 'form';
      setTimeout(() => {
        const forms = document.querySelectorAll(selector);
        if (forms.length > 0) {
          receiver.trackForm(forms, 'Form Submitted', {
            auto_tracked: true,
          });
          if (config.debug) {
            console.log(`[ReceiverLoader] Auto-tracking ${forms.length} forms`);
          }
        }
      }, 500);
    }

    // Auto-track outbound links
    if (config.trackOutboundLinks) {
      setTimeout(() => {
        const links = document.querySelectorAll(`a[href^="http"]:not([href*="${window.location.hostname}"])`);
        if (links.length > 0) {
          receiver.trackLink(links, 'Outbound Link Clicked', {
            auto_tracked: true,
            type: 'outbound',
          });
          if (config.debug) {
            console.log(`[ReceiverLoader] Auto-tracking ${links.length} outbound links`);
          }
        }
      }, 500);
    }

    // Auto-track widget interactions if configured
    if (config.autoTrackWidget) {
      this.setupWidgetTracking(receiver, config);
    }
  }

  /**
   * Set up automatic widget interaction tracking
   */
  private static setupWidgetTracking(receiver: any, config: ReceiverConfig): void {
    // Track widget open/close
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      // Track launcher button clicks
      if (target.closest('[data-tronic-launcher]')) {
        receiver.track('Widget Launcher Clicked', {
          auto_tracked: true,
          timestamp: new Date().toISOString(),
        });
      }

      // Track widget close button
      if (target.closest('[data-tronic-close]')) {
        receiver.track('Widget Closed', {
          auto_tracked: true,
          timestamp: new Date().toISOString(),
        });
      }

      // Track widget internal buttons
      if (target.closest('[data-tronic-widget] button')) {
        const button = target.closest('button') as HTMLButtonElement;
        receiver.track('Widget Button Clicked', {
          auto_tracked: true,
          button_text: button.textContent,
          button_id: button.id,
          timestamp: new Date().toISOString(),
        });
      }
    });

    if (config.debug) {
      console.log('[ReceiverLoader] Widget auto-tracking enabled');
    }
  }

  /**
   * Send initial page view automatically
   */
  private static sendInitialPageView(receiver: any, config: ReceiverConfig): void {
    try {
      // Wait a bit for the page to be fully loaded
      setTimeout(() => {
        const pageData = {
          path: window.location.pathname,
          url: window.location.href,
          title: document.title,
          referrer: document.referrer,
          timestamp: new Date().toISOString(),
          sdk_version: 'tronic-sdk',
          auto_track: true,
        };

        // Send page view as a track event instead of page event
        receiver.track('pageview', pageData);

        if (config.debug) {
          console.log('[ReceiverLoader] Sent initial page view:', pageData);
        }
      }, 100);
    } catch (error) {
      if (config.debug) {
        console.error('[ReceiverLoader] Failed to send initial page view:', error);
      }
    }
  }

  /**
   * Send page view (for manual tracking)
   */
  static trackPage(_pageName?: string, properties?: Record<string, any>): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for page tracking');
      return;
    }

    const pageData = {
      path: window.location.pathname,
      url: window.location.href,
      title: document.title,
      timestamp: new Date().toISOString(),
      sdk_version: 'tronic-sdk',
      ...properties,
    };

    receiver.track('pageview', pageData);
  }

  /**
   * Track user identification
   */
  static identifyUser(userId: string, traits?: Record<string, any>): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for user identification');
      return;
    }

    const identifyData = {
      timestamp: new Date().toISOString(),
      sdk_version: 'tronic-sdk',
      ...traits,
    };

    receiver.identify(userId, identifyData);
  }

  /**
   * Track custom event
   */
  static trackEvent(eventName: string, properties?: Record<string, any>): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for event tracking');
      return;
    }

    const eventData = {
      timestamp: new Date().toISOString(),
      sdk_version: 'tronic-sdk',
      ...properties,
    };

    receiver.track(eventName, eventData);
  }

  /**
   * Track click events on elements
   */
  static trackClick(selector: string | Element | Element[], eventName: string, properties?: Record<string, any>): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for click tracking');
      return;
    }

    receiver.trackClick(selector, eventName, properties);
  }

  /**
   * Track form submissions
   */
  static trackForm(
    selector: string | HTMLFormElement | HTMLFormElement[],
    eventName: string,
    properties?: Record<string, any>,
  ): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for form tracking');
      return;
    }

    receiver.trackForm(selector, eventName, properties);
  }

  /**
   * Track link clicks
   */
  static trackLink(selector: string | Element | Element[], eventName: string, properties?: Record<string, any>): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for link tracking');
      return;
    }

    receiver.trackLink(selector, eventName, properties);
  }

  /**
   * Track screen/view changes
   */
  static screen(name: string, properties?: Record<string, any>): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for screen tracking');
      return;
    }

    const screenData = {
      timestamp: new Date().toISOString(),
      sdk_version: 'tronic-sdk',
      ...properties,
    };

    receiver.screen(name, screenData);
  }

  /**
   * Group users by organization/team
   */
  static group(groupId: string, traits?: Record<string, any>): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for group tracking');
      return;
    }

    const groupData = {
      timestamp: new Date().toISOString(),
      sdk_version: 'tronic-sdk',
      ...traits,
    };

    receiver.group(groupId, groupData);
  }

  /**
   * Link multiple user identities
   */
  static alias(userId: string, previousId?: string): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for alias tracking');
      return;
    }

    receiver.alias(userId, previousId);
  }

  /**
   * Reset user data (typically on logout)
   */
  static resetUser(): void {
    const receiver = this.getReceiver();
    if (!receiver) {
      console.warn('[ReceiverLoader] Receiver not available for reset');
      return;
    }

    receiver.reset();
  }

  /**
   * Get the receiver instance
   */
  static getReceiver(): any {
    return (window as any).receiver;
  }

  /**
   * Check if receiver is initialized
   */
  static isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Reset initialization state (useful for testing)
   */
  static reset(): void {
    this.initialized = false;
    if (typeof window !== 'undefined') {
      delete (window as any).receiver;
    }
  }
}
